import { ISelectItem } from "@components/inputs/select/Select.types";
import { IFieldDefFn, IGetValueArgs, TGetValueFn } from "@components/smart/FieldInfo";
import { EmailDef, PhoneNumberDef, withDisplayName } from "@components/smart/GeneralFieldDefinition";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import SmartTableManager from "@components/smart/smartTable/SmartTableManager";
import {
    CompanyRoleEntity,
    EntitySetName,
    EntityTypeName,
    GeneralRoleEntity,
    GeneralRolePermissionEntity,
    ICompanyEntity,
    IUserEntity,
    IUserStatusEntity,
    UserCompanyRoleEntity,
    UserEntity
} from "@odata/GeneratedEntityTypes";
import { UserStatusCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { isObjectEmpty } from "@utils/general";
import i18next from "i18next";
import React from "react";
import { DefaultTheme } from "styled-components";

import { DASH_CHARACTER } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, LabelStatus, Sort } from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { createPath, IEntity } from "../../../odata/BindingContext";
import { ROUTE_USERS } from "../../../routes";
import TestIds from "../../../testIds";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import CompanyRolesList from "./CompanyRolesList";
import PermissionsOverview from "./PermissionsOverview";
import {
    activePath,
    CompaniesPath,
    COMPANY_ROLE_LINES_PATH,
    CompanyRolesPermissionsOverviewPath,
    CustomerGeneralRoleId,
    CustomerRolesPath,
    GeneralRolePermissionsOverviewPath,
    getCompanyRoleSelectItems,
    getUserStatusColor,
    isUserOwner,
    IUsersFormCustomData,
    RoleIdPath,
    TUsersFormStorage,
    userNameWithAvatarTableFormatter
} from "./Users.utils";
import UsersFormView from "./UsersFormView";

export function hasCompanies({ storage }: IGetValueArgs): boolean {
    return !!storage.context.getData().companies?.length;
}

const isCompanyRolesDisabled: TGetValueFn<boolean> = (args) => {
    return !!(args.storage as FormStorage).getBackendDisabledFieldMetadata(args.storage.data.bindingContext.navigate(UserEntity.CompanyRoles));
};

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        defaultFilters: [
            UserEntity.FirstName,
            UserEntity.LastName,
            UserEntity.Email,
            UserEntity.Status,
            UserEntity.GeneralRole
        ],
        filterDefinition: {
            FirstName: {},
            LastName: {},
            Email: {},
            ...withDisplayName(UserEntity.Status),
            ...withDisplayName(UserEntity.GeneralRole, GeneralRoleEntity.GeneralRoleName, {
                label: i18next.t("Users:SystemRole")
            }),
        },
        isValueHelp: true
    }];

    const table: ISplitPageTableDef = {
        filter: (args: IGetValueArgs) => {
            const filters = ["Id gt 0"];
            if (!!args.storage.context.getAddingNewCompany()) {
                filters.push(`${UserEntity.GeneralRole}/Id ne ${CustomerGeneralRoleId}`);
            }
            return filters.join(" AND ");
        },
        additionalProperties: [{
            id: UserEntity.CompanyRoles,
            additionalProperties: [{
                id: UserCompanyRoleEntity.CompanyRole
            }, {
                id: UserCompanyRoleEntity.Company
            }]
        }],
        filterBarDef,
        id: `${EntityTypeName.User}Table`,
        columns: [
            UserEntity.FirstName,
            UserEntity.GeneralRole,
            UserEntity.Email,
            UserEntity.Status,
            CustomerRolesPath
        ],
        columnDefinition: {
            FirstName: {
                label: i18next.t("Users:User"),
                width: BasicInputSizes.L,
                formatter: userNameWithAvatarTableFormatter,
                additionalProperties: [{ id: `/${UserEntity.LastName}` }]
            },
            Email: {},
            ...withDisplayName(UserEntity.GeneralRole, GeneralRoleEntity.GeneralRoleName, {
                label: i18next.t("Users:SystemRole"),
                additionalProperties: [{
                    id: GeneralRoleEntity.GeneralRolePermissions
                }]
            }),
            [CustomerRolesPath]: {
                isVisible: !!context.getCompanyId(),
                label: i18next.t("Users:AgendaRoles"),
                fieldSettings: {
                    disableSort: true
                },
                additionalProperties: [{
                    id: `${UserEntity.CompanyRoles}/${UserCompanyRoleEntity.CompanyRole}/${CompanyRoleEntity.CompanyRoleName}`
                }],
                formatter: (val: TValue, args: IFormatOptions) => {
                    const currentCompanyId = args.storage.context.getCompanyId();
                    const roles = (args.entity as IUserEntity).CompanyRoles
                        ?.filter(role => role.Company?.Id === currentCompanyId)
                        ?.map(role => role.CompanyRole?.CompanyRoleName) ?? [];
                    return roles.join(", ");
                }
            },
            [UserEntity.Status]: {
                fieldSettings: {
                    displayName: "Name"
                },
                formatter: (val, args) => {
                    const entity = args.entity as IUserEntity;
                    const title = entity.StatusCode !== UserStatusCode.WaitingForAcceptanceOfNewRole ? val as string : i18next.t("Users:Form.InvitedToRoleOwner");
                    const color: keyof DefaultTheme = getUserStatusColor(entity.StatusCode as UserStatusCode);

                    const colorWrapper = <ColoredText color={color}> {title} </ColoredText>;

                    return { tooltip: title, value: colorWrapper };
                }
            }
        },
        title: i18next.t("Users:Title"),
        initialSortBy: [{ id: UserEntity.FirstName, sort: Sort.Asc }]
    };

    const summary: ISummaryItem[] = [
        { id: UserEntity.Name },
        {
            id: "Status",
            label: i18next.t("Users:Form.Status"),
            formatter: (val: TValue) => {
                if (val) {
                    const status = val as IUserStatusEntity;
                    return status.Code !== UserStatusCode.WaitingForAcceptanceOfNewRole ? status.Name : i18next.t("Users:Form.InvitedToRoleOwner");
                }
                return DASH_CHARACTER;
            },
            colorFormatter: (val: TValue) => {
                const status = val as IUserStatusEntity;
                return getUserStatusColor(status?.Code as UserStatusCode);
            }
        }
    ];

    const form: IFormDef = {
        id: `${EntityTypeName.User}Form`,
        summary,
        title: i18next.t("Users:User"),
        translationFiles: getDefinitions.translationFiles,
        formControl: UsersFormView,
        isDeletable: true,
        additionalProperties: [{
            id: UserEntity.StatusCode
        }, {
            id: UserEntity.GeneralRole,
            additionalProperties: [{
                id: GeneralRoleEntity.GeneralRoleName
            }, {
                id: GeneralRoleEntity.GeneralRolePermissions,
                additionalProperties: [
                    { id: GeneralRolePermissionEntity.PermissionCode },
                    { id: GeneralRolePermissionEntity.IsEnabled }
                ]
            }]
        }, {
            id: UserEntity.CompanyRoles,
            additionalProperties: [{
                id: UserCompanyRoleEntity.CompanyRole,
                additionalProperties: [{ id: CompanyRoleEntity.CompanyRolePermissions }]
            }, {
                id: UserCompanyRoleEntity.Company
            }]
        }],
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("Users:NewUser"), storage.data.entity?.Name),
        fieldDefinition: {
            "FirstName": {},
            "LastName": {},
            "Email": {
                ...EmailDef
            },
            "PhoneNumber": {
                ...PhoneNumberDef
            },
            [activePath]: {
                type: FieldType.Switch,
                label: i18next.t("Common:General.Active"),
                isVisible: (args: IGetValueArgs) => {
                    const entity = args.storage.data.entity as IUserEntity;
                    return [UserStatusCode.Inactive, UserStatusCode.Active].includes(entity.StatusCode as UserStatusCode);
                },
                isDisabled: (args: IGetValueArgs) => {
                    const entity = args.storage.data.entity as IUserEntity;
                    const isOwner = isUserOwner(entity);
                    const table = SmartTableManager.getTable("UsersTable");
                    const ownersList = table?.getState()?.rows && Object.values(table.getState().rows).filter(row => {
                        return row.customData?.entity && isUserOwner(row.customData.entity);
                    });

                    return isOwner && ownersList?.length === 1;
                }
            },
            [UserEntity.GeneralRole]: {
                label: i18next.t("Users:Role"),
                type: FieldType.ComboBox,
                fieldSettings: {
                    displayName: GeneralRoleEntity.GeneralRoleName,
                    preloadItems: true, // todo: remove, find out why display name is not working
                    additionalProperties: [{ id: GeneralRoleEntity.GeneralRoleName }, {
                        id: GeneralRoleEntity.GeneralRolePermissions,
                        additionalProperties: [
                            { id: GeneralRolePermissionEntity.PermissionCode },
                            { id: GeneralRolePermissionEntity.IsEnabled }
                        ]
                    }],
                    itemsForRender: (items, args) => {
                        return items?.map(item => {
                            if (item.id === -1 && args.storage.context.getData().userSettings?.GeneralRole?.Id !== -1) {
                                item.isDisabled = true;
                            }
                            return item;
                        });
                    }
                },
                isDisabled: (args) => {
                    const isDisabled = !!(args.storage as FormStorage).getBackendDisabledFieldMetadata(args.storage.data.bindingContext.navigate(UserEntity.GeneralRole));

                    return isDisabled || (args.storage.getValueByPath(UserEntity.GeneralRole)?.Id === -1 &&
                        args.storage.context.getData().userSettings?.GeneralRole.Id !== -1);
                },
                affectedFields: [{ id: GeneralRolePermissionsOverviewPath }, { id: CompanyRolesPermissionsOverviewPath }]
            },
            [GeneralRolePermissionsOverviewPath]: {
                type: FieldType.Custom,
                label: i18next.t("Users:Form.GeneralRolesList"),
                labelStatus: LabelStatus.Removed,
                // isVisible: not(isCustomerGeneralRole),
                render: (args: IFieldDefFn) => {
                    const hasRole = !isObjectEmpty(args.storage.data.entity?.GeneralRole);
                    return (<>
                        {hasRole && <PermissionsOverview storage={args.storage as FormStorage}/>}
                    </>);
                }
            },
            [CompanyRolesPermissionsOverviewPath]: {
                type: FieldType.Custom,
                label: i18next.t("Users:Form.CompanyRolesList"),
                labelStatus: LabelStatus.Removed,
                isVisible: (args: IGetValueArgs) => {
                    const companyRoleLines = args.storage.getValueByPath(COMPANY_ROLE_LINES_PATH);
                    return !!companyRoleLines?.some((line: IEntity) => {
                        return !!line[CompaniesPath]?.length;
                    });
                },
                render: (args: IFieldDefFn) => {
                    return (
                        <CompanyRolesList
                            storage={args.storage as FormStorage<IUserEntity, IUsersFormCustomData>}/>);
                }
            },
            [createPath(COMPANY_ROLE_LINES_PATH, RoleIdPath)]: {
                type: FieldType.ComboBox,
                label: i18next.t("Users:Role"),
                isRequired: true,
                isCollectionField: true,
                width: BasicInputSizes.M,
                defaultValue: (args: IGetValueArgs) => {
                    return getCompanyRoleSelectItems(args.storage as TUsersFormStorage)
                        .find(item => !item.isDisabled)?.id;
                },
                fieldSettings: {
                    items: [],
                    itemsForRender: (origItems, args: IGetValueArgs) => {
                        const roleId: number = args.storage.getValue(args.bindingContext);
                        return getCompanyRoleSelectItems(args.storage as TUsersFormStorage, roleId);
                    }
                },
                isDisabled: isCompanyRolesDisabled
            },
            [createPath(COMPANY_ROLE_LINES_PATH, CompaniesPath)]: {
                type: FieldType.MultiSelect,
                label: i18next.t("Users:ForAgenda"),
                isRequired: true,
                isCollectionField: true,
                width: BasicInputSizes.XL,
                isDisabled: (args: IGetValueArgs) => {
                    const isDisabled = isCompanyRolesDisabled(args);
                    const bc = args.bindingContext.getParent().navigate(RoleIdPath);
                    return isDisabled || !args.storage.getValue(bc);
                },
                fieldSettings: {
                    keyPath: "Id",
                    tokenColor: "C_FIELD_fill",
                    items: [],
                    itemsForRender: (origItems, args: IGetValueArgs) => {
                        const companies = args.storage.context.getData().companies;
                        const items: ISelectItem<number>[] = [];
                        const usedCompaniesIds = args.storage.getValueByPath(COMPANY_ROLE_LINES_PATH)?.reduce((usedIds: number[], line: IEntity) => {
                            if (line[BindingContext.NEW_ENTITY_ID_PROP] === args.bindingContext.getParent().getKey()) {
                                return usedIds; // don't disable selected item
                            }
                            const lineCompanies = line[CompaniesPath]?.map((c: ICompanyEntity) => c.Id) ?? [];
                            return [...usedIds, ...lineCompanies];
                        }, []) ?? [];
                        companies?.forEach((company: ICompanyEntity) => {
                            items.push({
                                label: company.Name,
                                id: company.Id,
                                isDisabled: usedCompaniesIds.includes(company.Id)
                            });
                        });
                        return items;
                    }
                }
            }
        },
        groups: [{
            id: "general",
            rows: [
                [{ id: UserEntity.FirstName }, { id: UserEntity.LastName }, { id: UserEntity.Email }, { id: UserEntity.PhoneNumber }, { id: activePath }]
            ]
        }, {
            id: "systemRoles",
            title: i18next.t("Users:SystemRole"),
            rows: [[{ id: UserEntity.GeneralRole }]]
        }, {
            id: "systemPermissions",
            title: i18next.t("Users:SystemPermissions"),
            rows: [[{ id: GeneralRolePermissionsOverviewPath }]],
            customizationData: {
                isLocked: true
            }
        }, {
            id: COMPANY_ROLE_LINES_PATH,
            title: i18next.t("Users:AgendaRoles"),
            isVisible: hasCompanies,
            lineItems: {
                collection: COMPANY_ROLE_LINES_PATH,
                order: null,
                columns: [{ id: RoleIdPath }, { id: CompaniesPath }],
                isTheOnlyItemRemovable: true,
                canReorder: false,
                isItemCloneable: false,
                isDisabled: isCompanyRolesDisabled,
                isAddDisabled: (args: IGetValueArgs) => {
                    const { storage } = args;
                    const items = getCompanyRoleSelectItems(storage as TUsersFormStorage);
                    const rows = storage.getValueByPath(COMPANY_ROLE_LINES_PATH);
                    return (rows?.length ?? 0) === items?.length;
                }
            }
        }, {
            id: "companyRolesOverview",
            title: i18next.t("Users:CompanyPermissions"),
            isVisible: hasCompanies,
            rows: [[{ id: CompanyRolesPermissionsOverviewPath }]],
            customizationData: {
                isLocked: true
            }
        }]
    };

    return {
        entitySet: EntitySetName.Users,
        table,
        form
    };
};

getDefinitions.translationFiles = ["Users"];
setDefByEntityType(EntityTypeName.User, getDefinitions);
